import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { X, User, Award, Send } from 'lucide-react';
import toast from 'react-hot-toast';

function QuoteRequestModal({ row, onClose }) {
  const queryClient = useQueryClient();
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);

  const { data: iqps, isLoading } = useQuery({
    queryKey: ['workers', 'iqp', row.ss_code],
    queryFn: async () => {
      const res = await api.get(`/workers/iqp/${row.ss_code}`);
      return res.data?.data || [];
    },
    enabled: !!row?.ss_code,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/jobs/${row.job_id}/quote-request`, {
        iqpWorkerId: selectedWorkerId,
        defectNoticeId: row.defect_notice_id || undefined,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Quote request sent to IQP');
      queryClient.invalidateQueries(['repairs']);
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to send quote request');
    },
  });

  const handleSubmit = () => {
    if (!selectedWorkerId) {
      toast.error('Select an IQP');
      return;
    }
    sendMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Request Quote from IQP</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            {row.building_name} · {row.ss_code} {row.ss_name}
          </p>
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading IQPs...</div>
          ) : iqps && iqps.length > 0 ? (
            <div className="space-y-2">
              {iqps.map((w) => (
                <div
                  key={w.workerId}
                  onClick={() => setSelectedWorkerId(w.workerId)}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedWorkerId === w.workerId
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{w.workerName}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                          <span className="flex items-center">
                            <Award className="w-4 h-4 mr-1" />
                            Grade: {w.grade}
                          </span>
                          {w.workerEmail && <span>{w.workerEmail}</span>}
                        </div>
                      </div>
                    </div>
                    {selectedWorkerId === w.workerId && (
                      <span className="text-primary-600 font-medium">Selected</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No IQPs available for this SS code</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-6 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={sendMutation.isLoading || !selectedWorkerId}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            {sendMutation.isLoading ? 'Sending...' : 'Send Quote Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuoteRequestModal;
